import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Workspace selector for page headers (D12): monogram + workspace name, static
 * label when the identity belongs to one workspace, dropdown otherwise. The
 * workspace is the only ambient scope — businesses are grouping labels inside
 * pages, never a navigation context (D32).
 */
const WorkspacePill: React.FC = () => {
  const { workspace, memberships, switchWorkspace } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const hasChoice = memberships.length > 1;

  if (!workspace) return null;

  const handleSelect = async (workspaceId: string): Promise<void> => {
    if (workspaceId === workspace.id) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await switchWorkspace(workspaceId);
      setOpen(false);
    } catch {
      toast.error('Could not switch workspace.');
    } finally {
      setSwitching(false);
    }
  };

  const pillInner = (
    <>
      <span className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/15 text-primary text-xs font-semibold select-none flex-shrink-0">
        {monogram(workspace.name)}
      </span>
      <span className="text-sm font-medium text-foreground truncate">{workspace.name}</span>
    </>
  );

  if (!hasChoice) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card max-w-[240px]">
        {pillInner}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-accent transition-colors max-w-[240px]"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {pillInner}
        <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-1 w-56 max-h-72 overflow-y-auto bg-card rounded-lg shadow-lg border border-border z-20 py-1"
            role="listbox"
          >
            {memberships.map((m) => {
              const isActive = m.workspace.id === workspace.id;
              return (
                <button
                  key={m.workspace.id}
                  role="option"
                  aria-selected={isActive}
                  disabled={switching}
                  onClick={() => handleSelect(m.workspace.id)}
                  className={clsx(
                    'flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors',
                    isActive ? 'text-primary font-medium' : 'text-foreground hover:bg-accent',
                  )}
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/15 text-primary text-xs font-semibold select-none flex-shrink-0">
                    {monogram(m.workspace.name)}
                  </span>
                  <span className="truncate flex-1 text-left">{m.workspace.name}</span>
                  {switching && !isActive ? (
                    <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  ) : isActive ? (
                    <Check className="w-4 h-4 flex-shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

function monogram(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

export default WorkspacePill;
